import angular, { IPromise } from 'angular'
import VendNumber, {
  add,
  divide,
  multiply,
  round,
  subtract,
  vn,
} from 'vend-number'
import { PAYMENT_TYPES } from '@vend/business-components'
import { trackError } from '@vend/utilities'
import { QRCODE_OPTIONS } from '../components/receipts/selected-receipts.constant'
import { addSearchParams } from '../functions/url-utils'
import { Sale } from '../models/sale'
import {
  getOutstandingBalance,
  isCompleted,
  isMarkedAsUnfulfilled,
  isValidCustomer,
} from '../functions/sale'
import { store } from '../state/store'
import { LineItem } from '../models/line-item'
import {
  selectCurrentSale,
  selectLineItems,
} from '../state/slices/currentSale/currentSaleSlice'
import { retailerSettingsManager } from '../ng-compat/managers'
import { loyaltySettingsStore } from '../factories/database/loyalty-settings-store'
import { RetailerLoyaltySettings } from '../queries/loyalty-threshold'
import { getFeatureFromCache } from '../util/feature-cache'
import { FEATURE_XEO_2536_STORE_CREDIT_EARNS_LOYALTY } from '../constants/wr-features'
import { SyncData } from './../factories/database/entity-store'
import { customerBalancesManager } from './customer-balances-manager'

/**
 * Manages loyalty settings via an in-memory cache backed by IndexedDB.
 * Keeps the settings object synchronized with retailer settings updates from the RetailerSettingsManager.
 */
export class LoyaltyManager {
  private loyaltySettings: RetailerLoyaltySettings = {} as RetailerLoyaltySettings

  // ============================== Public =============================== //

  /**
   * Initialise the loyalty manager by loading settings from the store and
   * subscribing to store sync events so we keep an in-memory copy.
   *
   * @return {Promise} A promise resolved once the manager has been initialised.
   *
   */
  public init(): IPromise<RetailerLoyaltySettings> {
    return loyaltySettingsStore
      .get()
      .then((settings: RetailerLoyaltySettings) => {
        this.loyaltySettings = settings
        loyaltySettingsStore.on('sync', (event: SyncData<'settings'>) => {
          this.loyaltySettings = event.updated[0]
        })
        return settings
      })
  }

  /**
   * Get the in-memory loyalty settings.
   *
   * @method getLoyaltySettings
   *
   * @return {RetailerLoyaltySettings}
   * In-memory stored retailer loyalty settings.
   *
   */
  public getLoyaltySettings(): RetailerLoyaltySettings {
    return this.loyaltySettings
  }

  /**
   * Persist and update retailer loyalty settings with the loyalty store. Returns the
   * result of the store sync promise.
   *
   * @method setLoyaltySettings
   *
   * @param  {RetailerLoyaltySettings} [settings]
   * The payload of set action, updated loyalty settings
   *
   * @return {RetailerLoyaltySettings}
   * Result of the store sync promise
   *
   */
  public setLoyaltySettings(
    settings: RetailerLoyaltySettings
  ): IPromise<SyncData<'settings'>> {
    this.loyaltySettings = settings
    return loyaltySettingsStore.syncData([settings])
  }

  /**
   * Responsible for determinig if loyalty can be displayed on receipts.
   *
   * @method canDisplayReceiptLoyalty
   *
   * @param  {Sale} [retrievedSale]
   *         The Sale to check conditions against.
   *
   * @return {Boolean} True if loyalty can be displayed, false otherwise.
   */
  public canDisplayReceiptLoyalty(): boolean {
    const sale = selectCurrentSale(store.getState())

    if (
      !retailerSettingsManager.isLoyaltyEnabled() ||
      !sale ||
      (!isCompleted(sale.status) && !isMarkedAsUnfulfilled(sale.status))
    ) {
      return false
    }

    if (!isValidCustomer(sale.customer)) {
      // We always display loyalty on receipt if no customer (to encourage them to sign up for loyalty)
      return true
    }

    // We have a customer, only display loyalty on receipt if they have loyalty enabled
    return sale.customer.enable_loyalty
  }

  /**
   * Determines if there is loyalty earned on sale
   *
   * @method saleHasLoyaltyEarned
   *
   * @param  {Sale} [retrievedSale]
   *         The Sale to check conditions against.
   *
   * @return {Boolean} True if there is loyalty earned on the current, false otherwise
   */
  public saleHasLoyaltyEarned(): boolean {
    return Boolean(vn(this.getTotalLoyalty()).gt(0))
  }

  /**
   * Determines if there is loyalty deducted on sale
   *
   * @method saleHasLoyaltyDeducted
   *
   * @param  {Sale} [retrievedSale]
   *         The Sale to check conditions against.
   *
   * @return {Boolean} True if there is loyalty deducted on the current sale, false otherwise
   */
  public saleHasLoyaltyDeducted(): boolean {
    return Boolean(vn(this.getTotalLoyalty()).lt(0))
  }

  /**
   * Responsible for constructing the claim url for loyalty. Used when the sale has no customer, or when the sale
   * has a customer with no email address.
   *
   * When a custom loyalty claim URL is configured in retailer settings, it will use that URL.
   * Otherwise, it falls back to the default URL pattern: https://{host}/loyalty/claim/{shortCode}
   *
   * @method getLoyaltyClaimURL
   *
   * @param  {Sale} [retrievedSale]
   *         The Sale to check conditions against.
   *
   * @return {URL} The loyalty claim url
   */
  public getLoyaltyClaimURL(retrievedSale?: Sale): URL {
    const claimUrl = retailerSettingsManager.getLoyaltyClaimUrl()
    const sale = retrievedSale || selectCurrentSale(store.getState())
    const defaultUrl = new URL(
      `https://${window.location.host}/loyalty/claim/${sale.shortCode}`
    )

    if (claimUrl) {
      try {
        const dynamicUrl = claimUrl.replace('_shortcode_', sale.shortCode ?? '')
        return new URL(dynamicUrl)
      } catch (error) {
        trackError(
          '[LoyaltyManager] Invalid loyalty claim URL, falling back to legacy URL',
          {
            error,
          }
        )
      }
    }

    return defaultUrl
  }

  /**
   * QR Code is only shown on the receipt when the sale has no customer
   * or the customer has no email address.
   *
   * @method getLoyaltyClaimQRCodeURL
   *
   * @param  {loyaltyClaimUrl} Loyalty claim url to be encoded as qrcode
   *
   * @param  {size} Size in px for the generated qrcode (height=width=size)
   *
   * @return {URL} The loyalty claim qrcode url (vend/barcode - qrcode)
   */
  public getLoyaltyClaimQRCodeURL(
    loyaltyClaimUrl: URL,
    size: number = QRCODE_OPTIONS.defaultSize
  ): URL {
    return addSearchParams(new URL(`/images/qrcode`, loyaltyClaimUrl), {
      content: loyaltyClaimUrl,
      size,
    })
  }

  /**
   * Calculates the total amount of loyalty earned for a sale.
   *
   * @method getTotalLoyalty
   *
   * @param  {Sale} [retrievedSale]
   *         The Sale to check conditions against.
   *
   * @return {Number} The calculated total loyalty for the sale
   */
  public getTotalLoyalty(
    // TODO: Remove this parameter once receipt-print-selected.contoller has been reduxified
    retrievedSale?: Readonly<Sale>
  ): string | 0 {
    const sale = retrievedSale || selectCurrentSale(store.getState())

    if (!sale) {
      trackError('[LoyaltyManager] No active SaleManager sale')
      return 0
    }

    const totalToPay = sale.finals.inclusivePrice
    const totalLoyaltyPayments = customerBalancesManager.getTotalPartialPayments(
      PAYMENT_TYPES.LOYALTY,
      sale,
      true
    )
    // Store credit payments don't earn loyalty by default, so exclude the portion
    // of the sale paid with store credit from the loyalty-earning base, the same
    // way loyalty-paid amounts are excluded above. Behind
    // FEATURE_XEO_2536_STORE_CREDIT_EARNS_LOYALTY, store credit is treated as an
    // earning payment method instead, so it's excluded from this base.
    const storeCreditEarnsLoyalty = getFeatureFromCache(
      FEATURE_XEO_2536_STORE_CREDIT_EARNS_LOYALTY
    )
    const totalStoreCreditPayments = storeCreditEarnsLoyalty
      ? 0
      : customerBalancesManager.getTotalPartialPayments(
          PAYMENT_TYPES.STORE_CREDIT,
          sale,
          true
        )
    const totalNonEarningPayments = add(
      totalLoyaltyPayments,
      totalStoreCreditPayments
    )

    let lineItems = [] as ReadonlyArray<Readonly<LineItem>>
    if (!retrievedSale) {
      lineItems = selectLineItems(store.getState())
    } else {
      lineItems = retrievedSale.lineItems
    }

    const totalSaleLoyalty = lineItems.reduce(
      (total: number, lineItem: LineItem) => {
        return add(total, multiply(lineItem.quantity, lineItem.loyaltyValue))
      },
      0
    )
    let totalLoyalty = totalSaleLoyalty

    // Check if we have any non-loyalty-earning payments and that total to pay is not zero
    if (vn(totalNonEarningPayments).gt(0) && !vn(totalToPay).isZero()) {
      totalLoyalty = multiply(
        1 - divide(totalNonEarningPayments, totalToPay),
        totalSaleLoyalty
      )
    }

    return round(totalLoyalty, 2, VendNumber.ROUND_DOWN)
  }

  /**
   * Calculate the loyalty balance for the customer. If the sale is not completed, this is calculated as the
   * current loyalty balance minus the total loyalty payments for the sale (adjustedLoyaltyBalance). Otherwise
   * calculated as adjustedLoyaltyBalance plus the total loyalty earned for current sale.
   *
   * @method getCustomerLoyaltyBalance
   *
   * @param  {Sale} [retrievedSale]
   *         The Sale to check conditions against.
   *
   * @return {Number} The customer loyalty balanace
   */
  public getCustomerLoyaltyBalance(retrievedSale?: Sale): number {
    // TODO: Remove sale parameter once receipt-print-selected.contoller has been reduxified
    const sale = retrievedSale || selectCurrentSale(store.getState())
    const currentSaleCustomer = sale && sale.customer

    if (!currentSaleCustomer) {
      return 0
    }

    const totalLoyaltyPayments = customerBalancesManager.getTotalPartialPayments(
      PAYMENT_TYPES.LOYALTY,
      sale
    )
    const adjustedLoyaltyBalance = subtract(
      currentSaleCustomer.loyalty_balance || 0,
      totalLoyaltyPayments
    )

    const result =
      isCompleted(sale.status) ||
      (isMarkedAsUnfulfilled(sale.status) && getOutstandingBalance(sale) <= 0)
        ? // In the instance where the sale has been completed, but not yet posted to the API (such as Sale Summary)
          add(adjustedLoyaltyBalance, this.getTotalLoyalty())
        : adjustedLoyaltyBalance

    return Number(round(result, 2, VendNumber.ROUND_DOWN))
  }

  /**
   * Calculate the amount of loyalty customer needs to earn to reach the threshold.
   * Calculates retailer's loyalty threshold value minus current customer loyalty balance
   *
   * @method getCustomerLoyaltyThresholdRedemption
   *
   * @param  {Sale} [retrievedSale]
   * The Sale to check the customer loyalty balance
   *
   * @return {Number}
   * The threshold redemption
   *
   */
  public getCustomerLoyaltyThresholdRedemption(retrievedSale?: Sale): number {
    const currentLoyaltyBalance = this.getCustomerLoyaltyBalance(retrievedSale)

    const rawThreshold =
      this.loyaltySettings &&
      this.loyaltySettings.thresholdSettings &&
      this.loyaltySettings.thresholdSettings.value

    if (
      rawThreshold === null ||
      rawThreshold === undefined ||
      rawThreshold === ''
    ) {
      return 0
    }

    return subtract(rawThreshold, currentLoyaltyBalance)
  }
}

export const loyaltyManager = new LoyaltyManager()

/**
 * Responsible for managing loyalty for the current sale
 *
 * @module loyaltyManager
 */
angular
  .module('webregister.managers')
  .factory('loyaltyManager', () => loyaltyManager)
